import DOMPurify from "dompurify";

const BASE_STYLE = `
html { color-scheme: light; background: #ffffff; }
body {
  margin: 0;
  padding: 16px 18px;
  background: #ffffff;
  color: #1c1917;
  font-family: "Segoe UI", Calibri, "Helvetica Neue", Arial, sans-serif;
  font-size: 16px;
  line-height: 1.55;
  overflow-wrap: anywhere;
}
img, video { max-width: 100%; }
table { max-width: 100%; }
`.trim();

/**
 * Systemfarben auf hell festlegen und Techniken abschalten, die Schrift
 * unsichtbar machen. Konkrete Farben der Mail bleiben hier unangetastet.
 */
const GUARD_STYLE = `
html, body, body * {
  color-scheme: only light !important;
}
* {
  -webkit-text-fill-color: currentcolor !important;
  mix-blend-mode: normal !important;
}
`.trim();

const DARK_TEXT = "#1c1917";
const LIGHT_TEXT = "#f8fafc";
const DARK_RGB: Rgb = { r: 28, g: 25, b: 23, a: 1 };
const LIGHT_RGB: Rgb = { r: 248, g: 250, b: 252, a: 1 };
/** Darunter ist Text auf dem gemalten Hintergrund praktisch nicht mehr lesbar. */
const MIN_CONTRAST = 1.7;

type Rgb = { r: number; g: number; b: number; a: number };

let linkHooksReady = false;

function ensureLinkHooks(): void {
  if (linkHooksReady) return;
  linkHooksReady = true;
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName !== "A") return;
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  });
}

function matchBrace(css: string, openIndex: number): number {
  let depth = 0;
  let quote = "";
  for (let i = openIndex; i < css.length; i += 1) {
    const ch = css[i] ?? "";
    if (quote) {
      if (ch === "\\") {
        i += 1;
        continue;
      }
      if (ch === quote) quote = "";
      continue;
    }
    if (ch === '"' || ch === "'") {
      quote = ch;
      continue;
    }
    if (ch === "/" && css[i + 1] === "*") {
      const end = css.indexOf("*/", i + 2);
      if (end === -1) return -1;
      i = end + 1;
      continue;
    }
    if (ch === "{") depth += 1;
    else if (ch === "}") {
      depth -= 1;
      if (depth === 0) return i;
    }
  }
  return -1;
}

function normalizePrelude(prelude: string): string {
  return prelude.replace(/\/\*[\s\S]*?\*\//g, "").toLowerCase().replace(/\s+/g, "");
}

function isDarkModeMedia(prelude: string): boolean {
  const normalized = normalizePrelude(prelude);
  if (!normalized.includes("@media") || !normalized.includes("prefers-color-scheme:dark")) return false;
  return !normalized.includes("prefers-color-scheme:light");
}

/** Helle Variante ohne weitere Bedingungen: im hellen Rahmen immer anwenden. */
function isPureLightModeMedia(prelude: string): boolean {
  const normalized = normalizePrelude(prelude);
  if (!normalized.includes("@media") || !normalized.includes("prefers-color-scheme:light")) return false;
  if (normalized.includes("prefers-color-scheme:dark") || normalized.includes("not")) return false;
  const rest = normalized
    .replace(/@media/g, "")
    .replace(/onlyscreenand/g, "")
    .replace(/screenand/g, "")
    .replace(/onlyscreen/g, "")
    .replace(/\(prefers-color-scheme:light\)/g, "")
    .replace(/prefers-color-scheme:light/g, "")
    .replace(/and/g, "")
    .replace(/[(),]/g, "");
  return rest === "";
}

function withoutDarkSelectors(prelude: string): string {
  const comments: string[] = [];
  const commentless = prelude.replace(/\/\*[\s\S]*?\*\//g, (comment) => {
    comments.push(comment);
    return "";
  });
  const parts = commentless.split(",");
  const kept = parts.filter((part) => {
    const normalized = part.toLowerCase().replace(/\s+/g, "");
    return !(
      normalized.includes("data-ogsc") ||
      normalized.includes("data-ogsb") ||
      normalized.includes("data-ogab") ||
      normalized.includes("data-ogac") ||
      normalized.includes("gmail-blend")
    );
  });
  if (kept.length === parts.length) return prelude;
  if (kept.every((part) => part.trim() === "")) return "";
  const restored = comments.length > 0 ? `${comments.join("")}${kept.join(",")}` : kept.join(",");
  return restored;
}

/** Entfernt Dark-Mode-Regeln, die auf weißem Grund unsichtbare Schrift erzeugen. */
export function stripUnreadableMailCss(css: string): string {
  let out = "";
  let i = 0;
  while (i < css.length) {
    const open = css.indexOf("{", i);
    if (open === -1) {
      out += css.slice(i);
      break;
    }
    const close = matchBrace(css, open);
    if (close === -1) {
      out += css.slice(i);
      break;
    }
    const prelude = css.slice(i, open);
    const body = css.slice(open + 1, close);
    if (isDarkModeMedia(prelude)) {
      i = close + 1;
      continue;
    }
    if (isPureLightModeMedia(prelude)) {
      out += stripUnreadableMailCss(body);
      i = close + 1;
      continue;
    }
    const keptPrelude = withoutDarkSelectors(prelude);
    if (keptPrelude.trim() === "") {
      i = close + 1;
      continue;
    }
    const normalized = normalizePrelude(keptPrelude);
    const nested =
      normalized.includes("@media") || normalized.includes("@supports") || normalized.includes("@layer");
    const nextBody = nested ? stripUnreadableMailCss(body) : body;
    out += `${keptPrelude}{${nextBody}}`;
    i = close + 1;
  }
  return out;
}

function stripColorSchemeDeclarations(css: string): string {
  return css.replace(/(?:supported-color-schemes|color-scheme)\s*:\s*[^;}{]+;?/gi, "");
}

function rewriteStyleTags(html: string): string {
  return html.replace(/<style\b([^>]*)>([\s\S]*?)<\/style>/gi, (_match, attrs: string, css: string) => {
    return `<style${attrs}>${stripColorSchemeDeclarations(stripUnreadableMailCss(css))}</style>`;
  });
}

function neutralizeInlineColorScheme(html: string): string {
  return html.replace(/\sstyle\s*=\s*(["'])([\s\S]*?)\1/gi, (full, quote: string, value: string) => {
    if (!/color-scheme/i.test(value)) return full;
    const next = value.replace(/(?:supported-color-schemes|color-scheme)\s*:\s*[^;"]+;?/gi, "").trim();
    if (!next) return "";
    return ` style=${quote}${next}${quote}`;
  });
}

function parseCssColor(value: string): Rgb | null {
  const normalized = value.trim().toLowerCase();
  if (normalized === "transparent") return { r: 0, g: 0, b: 0, a: 0 };
  const match = normalized.match(
    /^rgba?\(\s*([\d.]+)\s*[, ]\s*([\d.]+)\s*[, ]\s*([\d.]+)(?:\s*[,/]\s*([\d.]+%?))?\s*\)$/,
  );
  if (!match) return null;
  const channel = (raw: string) => Math.max(0, Math.min(255, Number(raw)));
  let alpha = 1;
  if (match[4] !== undefined) {
    alpha = match[4].endsWith("%") ? Number(match[4].slice(0, -1)) / 100 : Number(match[4]);
  }
  const red = match[1];
  const green = match[2];
  const blue = match[3];
  if (red === undefined || green === undefined || blue === undefined) return null;
  if (![red, green, blue].every((part) => Number.isFinite(Number(part)))) return null;
  if (!Number.isFinite(alpha)) return null;
  return {
    r: channel(red),
    g: channel(green),
    b: channel(blue),
    a: Math.max(0, Math.min(1, alpha)),
  };
}

function composite(fg: Rgb, bg: Rgb): Rgb {
  const alpha = fg.a + bg.a * (1 - fg.a);
  if (alpha <= 0) return { r: bg.r, g: bg.g, b: bg.b, a: 1 };
  return {
    r: (fg.r * fg.a + bg.r * bg.a * (1 - fg.a)) / alpha,
    g: (fg.g * fg.a + bg.g * bg.a * (1 - fg.a)) / alpha,
    b: (fg.b * fg.a + bg.b * bg.a * (1 - fg.a)) / alpha,
    a: alpha,
  };
}

function luminance(color: Rgb): number {
  const channel = (value: number) => {
    const s = value / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(color.r) + 0.7152 * channel(color.g) + 0.0722 * channel(color.b);
}

function contrast(a: Rgb, b: Rgb): number {
  const left = luminance(a);
  const right = luminance(b);
  const lighter = Math.max(left, right);
  const darker = Math.min(left, right);
  return (lighter + 0.05) / (darker + 0.05);
}

function contrastAgainst(text: Rgb, background: Rgb): number {
  const visible = text.a >= 0.999 ? text : composite(text, background);
  return contrast(visible, background);
}

function hasDirectText(el: Element): boolean {
  for (const node of el.childNodes) {
    if (node.nodeType !== 3) continue;
    if ((node.textContent ?? "").replace(/\u00a0/g, " ").trim()) return true;
  }
  return false;
}

function paintedBackground(el: Element, view: Window): Rgb | null {
  const layers: Rgb[] = [];
  let node: Element | null = el;
  while (node) {
    const style = view.getComputedStyle(node);
    if (style.filter && style.filter !== "none") return null;
    const image = style.backgroundImage;
    const bg = parseCssColor(style.backgroundColor);
    if (image && image !== "none") {
      if (bg && bg.a >= 0.95) {
        layers.push(bg);
        break;
      }
      return null;
    }
    if (!bg) {
      if (style.backgroundColor && style.backgroundColor !== "transparent") return null;
    } else if (bg.a > 0.01) {
      layers.push(bg);
      if (bg.a >= 0.95) break;
    }
    node = node.parentElement;
  }
  let result: Rgb = { r: 255, g: 255, b: 255, a: 1 };
  for (let index = layers.length - 1; index >= 0; index -= 1) {
    const layer = layers[index];
    if (!layer) continue;
    result = composite(layer, result);
  }
  return result;
}

const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEMPLATE", "HEAD", "TITLE", "META", "LINK"]);

/**
 * Hebt Schrift an, die auf dem tatsächlich gemalten Hintergrund untergeht.
 * Weiß auf Dunkelblau und andere gewollte Kombinationen bleiben erhalten.
 */
export function repairUnreadableMailColors(doc: Document): void {
  const view = doc.defaultView;
  const body = doc.body;
  const root = doc.documentElement;
  if (!view || !body || !root || root.dataset.mailContrast === "running") return;
  if (!doc.getElementById("campdesk-mail-guard")) return;
  root.dataset.mailContrast = "running";
  try {
    for (const el of [body, ...body.querySelectorAll("*")]) {
      if (SKIP_TAGS.has(el.tagName) || !hasDirectText(el)) continue;
      const style = view.getComputedStyle(el);
      if (style.display === "none" || style.visibility === "hidden" || style.visibility === "collapse") continue;
      const opacity = Number.parseFloat(style.opacity);
      if (Number.isFinite(opacity) && opacity < 0.15) continue;
      const fontSize = Number.parseFloat(style.fontSize);
      if (Number.isFinite(fontSize) && fontSize < 2) continue;
      const rect = el.getBoundingClientRect();
      if (rect.width < 1 || rect.height < 1) continue;
      const clip = style.backgroundClip || style.getPropertyValue("-webkit-background-clip");
      if (clip === "text") continue;
      const fill = parseCssColor(style.getPropertyValue("-webkit-text-fill-color"));
      const text = fill && fill.a > 0.05 ? fill : parseCssColor(style.color);
      if (!text) continue;
      const background = paintedBackground(el, view);
      if (!background) continue;
      const current = contrastAgainst(text, background);
      if (current >= MIN_CONTRAST) continue;
      const darkContrast = contrast(DARK_RGB, background);
      const lightContrast = contrast(LIGHT_RGB, background);
      const useDark = darkContrast >= lightContrast;
      const best = useDark ? darkContrast : lightContrast;
      if (best < current + 0.75) continue;
      const inline = (el as HTMLElement).style;
      if (!inline || typeof inline.setProperty !== "function") continue;
      const next = useDark ? DARK_TEXT : LIGHT_TEXT;
      inline.setProperty("color", next, "important");
      inline.setProperty("-webkit-text-fill-color", next, "important");
    }
  } finally {
    delete root.dataset.mailContrast;
  }
}

function visibleMailText(html: string): string {
  return html
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, " ")
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, " ")
    .replace(/<title\b[^>]*>[\s\S]*?<\/title>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;|&#160;|&#xa0;/gi, " ")
    .replace(/&[a-z#0-9]+;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function mailHtmlHasVisibleContent(html: string): boolean {
  if (visibleMailText(html).length > 0) return true;
  return /<img\b/i.test(html);
}

function injectFrameChrome(documentHtml: string): string {
  const base = `<meta charset="utf-8"><base target="_blank"><style id="campdesk-mail-base">${BASE_STYLE}</style>`;
  const guard = `<style id="campdesk-mail-guard">${GUARD_STYLE}</style>`;
  let next = documentHtml.replace(/<meta\b[^>]*color-scheme[^>]*>/gi, "");
  if (/<head\b[^>]*>/i.test(next)) {
    next = next.replace(/<head\b[^>]*>/i, (head) => `${head}${base}`);
  } else {
    next = `<!DOCTYPE html><html><head>${base}</head><body>${next}</body></html>`;
  }
  if (/<\/body>/i.test(next)) {
    next = next.replace(/<\/body>/i, `${guard}</body>`);
  } else {
    next += guard;
  }
  return next;
}

/** Eigenständiges HTML-Dokument für die isolierte Mail-Anzeige. */
export function buildMailFrameSrcDoc(html: string): string | null {
  const source = html.trim();
  if (!source) return null;
  ensureLinkHooks();
  const sanitized = DOMPurify.sanitize(source, {
    USE_PROFILES: { html: true },
    WHOLE_DOCUMENT: true,
    ADD_ATTR: ["target", "rel"],
  });
  if (!mailHtmlHasVisibleContent(sanitized)) return null;
  return injectFrameChrome(neutralizeInlineColorScheme(rewriteStyleTags(sanitized)));
}
